import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAppContext } from '@contexts/AppContext';
import { getOutletAbsoluteUrl, getShafaAbsoluteUrl } from '@utils/common';

import {
  ClerkInitPayload,
  ClerkPageViewEventPayload,
  ClerkEventPayload,
  ClerkData,
} from './types';

type PushPayloads = Record<string, unknown>;

declare const clerkStats: {
  push(params: PushPayloads, force: boolean): void;
  processPageview(obj: PushPayloads): void;
};

export const useClerk = () => {
  const history = useHistory();
  const location = useLocation();

  const referrerRef = useRef('');

  const { isOutlet } = useAppContext();

  const initClerk = useCallback(({ userId, abTests } : ClerkInitPayload) => {
    window.abTests = abTests || '';

    if (userId) {
      window.VID = userId;
    }    
  }, []);

  const getClerkClientId = useCallback(async (): Promise<string | undefined> => {
    setTimeout(() => window.postMessage({ clerk: { request: 'getInfo' } }, '*'));
  
    return new Promise((resolve) => {
      const eventHandler = ({ data }: ClerkData) => {
        const { clerk } = data;
        if (!clerk?.response) {
          return;
        }
  
        window.removeEventListener('message', eventHandler);
        resolve(clerk.response.client_id);
      };
  
      window.addEventListener('message', eventHandler);
    });
  }, []);

  const sendClerkPageViewEvent = useCallback((payload: ClerkPageViewEventPayload = {}) => {
    const localizedPathname = history.createHref({ pathname: location.pathname });

    try {
      clerkStats.push(
        {
          ab_test: window.abTests,
          ...(window.VID && { vid: window.VID }),
          ...payload,
          $: {
            referrer: referrerRef.current,
            url: isOutlet ? getOutletAbsoluteUrl(localizedPathname) : getShafaAbsoluteUrl(localizedPathname),
          },
          _: 'pageview',
        },
        true,
      );
    } catch (err) {
      console.log(err);
    }
  }, [history, isOutlet, location.pathname]);

  const sendClerkEvent = useCallback(<T extends ClerkEventPayload>({ category, action, payload }: T) => {
    try {
      clerkStats.push(
        {
          category,
          action,
          ab_test: window.abTests,
          ...(window.VID && { vid: window.VID }),
          ...payload,
          _: 'event',
        },
        true,
      );
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    if (!referrerRef.current) {
      referrerRef.current = document.referrer || 'unknown';
    }

    return () => {
      if (referrerRef.current) {
        const localizedPathname = history.createHref({ pathname: location.pathname });
        referrerRef.current = isOutlet
          ? getOutletAbsoluteUrl(localizedPathname)
          : getShafaAbsoluteUrl(localizedPathname);
      }
    };
  }, [history, isOutlet, location.pathname, location.search]);
  
  return {
    initClerk,
    getClerkClientId,
    sendClerkPageViewEvent,
    sendClerkEvent,
  };
};
